import React, { useCallback, useContext, useEffect } from 'react';
import { UserContext } from '.';
import { ClientFeatureUsageInfo, _PlanFeatureEnum } from '../clientContracts/plan/planDTO';
import { useFeatureUsageData } from '../hooks/useFeatureUsageData';
import { CreditLimit, FeatureUsageContextValue, FreeTrialLimit } from '../types/featureUsage';
import { requestHandler } from '../utils';

// Custom hook for feature usage actions
const useFeatureUsageActions = () => {
    const fetchFeatureUsage = useCallback(
        async (
            feature: _PlanFeatureEnum,
            totalActions: number,
        ): Promise<ClientFeatureUsageInfo | undefined> => {
            const response = await requestHandler<ClientFeatureUsageInfo>(
                'GET',
                `/api/plan/client-plan/feature-usage?feature=${feature}&totalActions=${totalActions}`,
            );
            return response.success ? response.payload : undefined;
        },
        [],
    );

    return { fetchFeatureUsage };
};

// Custom hook for feature calculations
const useFeatureCalculations = (featureUsages: ClientFeatureUsageInfo[]) => {
    const canPerformFeatureAction = useCallback(
        (feature: _PlanFeatureEnum, totalActions: number): CreditLimit => {
            const featureUsage = featureUsages.find((f) => f.feature === feature);

            // Early return if feature has no usage restrictions
            if (!featureUsage || featureUsage.subtype === 'DEFAULT' || !featureUsage.usageCap) {
                return {
                    isCreditLimitReached: false,
                    feature,
                    exceededCap: 0,
                    usageCap: 0,
                };
            }

            const exceededCap = featureUsage.usage + totalActions - featureUsage.usageCap;
            const baseCreditLimit = {
                feature,
                usageCap: featureUsage.usageCap,
                exceededCap: Math.max(0, exceededCap),
            };

            // Handle case where usage would exactly hit the cap
            if (exceededCap === totalActions) {
                return {
                    ...baseCreditLimit,
                    isCreditLimitReached: true,
                    exceededCap: 0,
                };
            }

            return {
                ...baseCreditLimit,
                isCreditLimitReached: featureUsage.subtype === 'HARD_CAP' && exceededCap > 0,
            };
        },
        [featureUsages],
    );

    return { canPerformFeatureAction };
};

// Default context value
const defaultContextValue: FeatureUsageContextValue = {
    fetchFeatureUsage: () => Promise.resolve(undefined),
    featureUsages: [],
    usages: [],
    clientPlan: undefined,
    canPerformFeatureAction: () => ({
        isCreditLimitReached: false,
        feature: '',
        exceededCap: 0,
        usageCap: 0,
    }),
    getUsageInfo: () => undefined,
    freeTrialLimitReached: { isFreeTrialLimitReached: false, feature: '' },
    creditLimitReached: { isCreditLimitReached: false, feature: '', exceededCap: 0, usageCap: 0 },
    setFreeTrialLimitReached: () => {
        // Do nothing
    },
    setCreditLimitReached: () => {
        // Do nothing
    },
    refreshFeatureUsage: () => Promise.resolve(),
    isFeatureUsageActive: false,
    plans: [],
    clientPlanWithUsage: undefined,
    accountManagers: [],
    updateClientPlan: () => {
        // Do nothing
    },
    fetchAllFeatureUsage: () => Promise.resolve(),
    updateFeatureUsage: () => Promise.resolve(),
};

const FeatureUsageContext = React.createContext<FeatureUsageContextValue>(defaultContextValue);

interface FeatureUsageProviderProps {
    children: React.ReactNode;
}

const FeatureUsageProvider: React.FC<FeatureUsageProviderProps> = ({ children }) => {
    const { activeClient } = useContext(UserContext);
    const [freeTrialLimitReached, setFreeTrialLimitReached] = React.useState<FreeTrialLimit>({
        isFreeTrialLimitReached: false,
        feature: '',
    });
    const [creditLimitReached, setCreditLimitReached] = React.useState<CreditLimit>({
        isCreditLimitReached: false,
        feature: '',
        exceededCap: 0,
        usageCap: 0,
    });
    const {
        featureUsages,
        usages,
        clientPlan,
        fetchAllFeatureUsage,
        plans,
        clientPlanWithUsage,
        refreshFeatureUsage,
        accountManagers,
        updateClientPlan,
        updateFeatureUsage,
    } = useFeatureUsageData();

    const { fetchFeatureUsage } = useFeatureUsageActions();
    const { canPerformFeatureAction } = useFeatureCalculations(featureUsages);

    const getUsageInfo = useCallback(
        (feature: _PlanFeatureEnum) => {
            return usages.find((usage) => usage.feature === feature);
        },
        [usages],
    );

    useEffect(() => {
        if (!activeClient?.id) return;
        fetchAllFeatureUsage();
    }, [activeClient?.id, fetchAllFeatureUsage]);

    const handleSetFreeTrialLimitReached = useCallback((value: FreeTrialLimit) => {
        setFreeTrialLimitReached({
            isFreeTrialLimitReached: value.isFreeTrialLimitReached,
            feature: value.feature || '',
        });
    }, []);

    const handleSetCreditLimitReached = useCallback((value: CreditLimit) => {
        setCreditLimitReached({
            isCreditLimitReached: value.isCreditLimitReached,
            feature: value.feature || '',
            exceededCap: value.exceededCap || 0,
            usageCap: value.usageCap || 0,
        });
    }, []);

    const contextValue: FeatureUsageContextValue = {
        fetchFeatureUsage,
        featureUsages,
        usages,
        getUsageInfo,
        clientPlan,
        canPerformFeatureAction,
        freeTrialLimitReached,
        creditLimitReached,
        setFreeTrialLimitReached: handleSetFreeTrialLimitReached,
        setCreditLimitReached: handleSetCreditLimitReached,
        refreshFeatureUsage,
        isFeatureUsageActive: !!clientPlan?.plan.planFeatures?.length,
        plans,
        clientPlanWithUsage,
        accountManagers,
        updateClientPlan,
        fetchAllFeatureUsage,
        updateFeatureUsage,
    };

    return (
        <FeatureUsageContext.Provider value={contextValue}>{children}</FeatureUsageContext.Provider>
    );
};

export { FeatureUsageContext, FeatureUsageProvider };
